import type { Bounds, Movie } from "./types";

export type HallSize = "any" | "small" | "medium" | "large";
export type Depth = "back" | "middle" | "front";
export type Side = "center" | "sides";
export type Layout = "any" | "row" | "two-rows";
export type StepKey = "film" | "size" | "depth" | "side" | "group" | "recap";

export type Criteria = {
  movieSlug: string;
  hallSize: HallSize;
  /** Zones acceptées : tout cocher revient à n'imposer aucune contrainte. */
  depth: Depth[];
  side: Side[];
  people: number;
  layout: Layout;
};

export const ALL_DEPTHS: Depth[] = ["back", "middle", "front"];
export const ALL_SIDES: Side[] = ["center", "sides"];

export type Choice<T> = { value: T; label: string; hint?: string; disabled?: boolean };

export const MAX_PEOPLE = 8;

/** En dessous, une salle a trop peu de rangées pour parler de « milieu » ou de « côtés ». */
const SMALL_HALL_SEATS = 120;

export const defaultCriteria = (movieSlug: string): Criteria => ({
  movieSlug,
  hallSize: "any",
  depth: [...ALL_DEPTHS],
  side: [...ALL_SIDES],
  people: 2,
  layout: "any",
});

/**
 * Bascule une zone. Le vide est autorisé — sinon, passer de « milieu » à « haut de salle »
 * obligerait à cocher avant de décocher. C'est le bouton Suivant qui bloque.
 */
export function toggle<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export const largestHall = (movie: Movie) =>
  Math.max(0, ...movie.showtimes.map((showtime) => showtime.capacity ?? 0));

export const isCompact = (movie: Movie) => largestHall(movie) < SMALL_HALL_SEATS;

export function matchesHallSize(capacity: number, size: HallSize, bounds: Bounds) {
  if (size === "any" || !bounds) return true;
  if (size === "small") return capacity <= bounds.lower;
  if (size === "large") return capacity >= bounds.upper;
  return capacity > bounds.lower && capacity < bounds.upper;
}

/** Traduit le palier en bornes concrètes : le serveur filtre sur des capacités, pas sur des mots. */
export function capacityRange(bounds: Bounds, size: HallSize) {
  if (!bounds || size === "any") return {};
  if (size === "small") return { maxCapacity: bounds.lower };
  if (size === "large") return { minCapacity: bounds.upper };
  return { minCapacity: bounds.lower + 1, maxCapacity: bounds.upper - 1 };
}

export function hallsFor(movie: Movie, size: HallSize, bounds: Bounds) {
  const matching = movie.showtimes.filter((showtime) =>
    matchesHallSize(showtime.capacity ?? 0, size, bounds),
  );
  return {
    count: matching.length,
    halls: [...new Set(matching.map((showtime) => showtime.auditorium))],
  };
}

export function hallSizeChoices(movie: Movie, bounds: Bounds): Choice<HallSize>[] {
  const any: Choice<HallSize> = { value: "any", label: "Peu importe", hint: "toutes les salles" };
  if (!bounds) return [any];

  const build = (value: HallSize, label: string, range: string): Choice<HallSize> => ({
    value,
    label,
    hint: range,
    disabled: hallsFor(movie, value, bounds).count === 0,
  });

  return [
    any,
    build("small", "Petite", `≤ ${bounds.lower} pl.`),
    ...(bounds.upper - bounds.lower > 1
      ? [build("medium", "Moyenne", `${bounds.lower + 1}–${bounds.upper - 1} pl.`)]
      : []),
    build("large", "Grande", `≥ ${bounds.upper} pl.`),
  ];
}

export function depthChoices(movie: Movie): Choice<Depth>[] {
  return [
    { value: "back", label: "Haut de salle", hint: "tout au fond" },
    ...(isCompact(movie) ? [] : [{ value: "middle" as const, label: "Milieu", hint: "entre les deux" }]),
    { value: "front", label: "Bas de salle", hint: "près de l'écran" },
  ];
}

export const sideChoices = (): Choice<Side>[] => [
  { value: "center", label: "Au centre" },
  { value: "sides", label: "Sur les côtés" },
];

export function layoutChoices(people: number): Choice<Layout>[] {
  const front = Math.ceil(people / 2);
  const back = Math.floor(people / 2);

  return [
    {
      value: "row",
      label: "Sur une rangée",
      hint: people < 2 ? "une place" : `${people} sièges alignés`,
      disabled: people < 2,
    },
    {
      value: "two-rows",
      label: "Sur deux rangées",
      hint: people < 3 ? "à partir de 3 personnes" : `${front} devant, ${back} derrière`,
      disabled: people < 3,
    },
    { value: "any", label: "Peu importe", hint: "places proches", disabled: people < 2 },
  ];
}

export function stepsFor(movie: Movie | undefined): StepKey[] {
  const compact = movie ? isCompact(movie) : false;
  return (["film", "size", "depth", "side", "group", "recap"] as StepKey[]).filter(
    (step) => step !== "side" || !compact,
  );
}

/** Recadre les choix devenus impossibles après un changement (salle plus petite, groupe réduit). */
export function reconcile(criteria: Criteria, movie: Movie, bounds: Bounds): Criteria {
  const allowed = <T extends string>(choices: Choice<T>[], value: T): T =>
    choices.some((choice) => choice.value === value && !choice.disabled) ? value : ("any" as T);

  const zones = depthChoices(movie).map((choice) => choice.value);

  return {
    ...criteria,
    hallSize: allowed(hallSizeChoices(movie, bounds), criteria.hallSize),
    depth: criteria.depth.filter((zone) => zones.includes(zone)),
    side: isCompact(movie) ? [...ALL_SIDES] : criteria.side,
    layout: criteria.people < 2 ? "any" : allowed(layoutChoices(criteria.people), criteria.layout),
  };
}

export const STEP_LABELS: Record<StepKey, string> = {
  film: "Film",
  size: "Taille de salle",
  depth: "Où s'asseoir",
  side: "Placement latéral",
  group: "Combien et comment",
  recap: "Récapitulatif",
};

export const DEPTH_LABELS: Record<Depth, string> = {
  back: "Haut de salle",
  middle: "Milieu",
  front: "Bas de salle",
};

export const SIDE_LABELS: Record<Side, string> = {
  center: "Au centre",
  sides: "Sur les côtés",
};

export const zonesLabel = (values: string[], labels: Record<string, string>, whenAll: string) => {
  if (values.length === 0) return "aucune zone";
  return values.length >= Object.keys(labels).length
    ? whenAll
    : values.map((value) => labels[value]).join(", ");
};

export const LAYOUT_LABELS: Record<Layout, string> = {
  any: "Peu importe",
  row: "Sur une rangée",
  "two-rows": "Sur deux rangées",
};
